import axios from 'axios';
import config from '../config';

const { api } = config;

const checkListAxios = axios.create({
  baseURL: `${api.API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
checkListAxios.interceptors.request.use(
  (config) => {
    const authUser = sessionStorage.getItem('authUser');
    if (authUser) {
      const user = JSON.parse(authUser);
      if (user && user.token) {
        config.headers.Authorization = `Bearer ${user.token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
checkListAxios.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message = error.response?.data?.message || error.message || 'Erro na requisição';
    return Promise.reject(new Error(message));
  }
);

// Types
export interface CheckListItem {
  id: string;
  topicId: string;
  name: string;
  description?: string;
  responsible: string;
  deadlineValue: number;
  deadlineUnit: string;
  observation?: string;
  displayOrder: number;
  skiped: boolean;
  dependencyItemIds?: string[]; // IDs of items this item depends on (Condição)
}

export interface CheckListTopic {
  id: string;
  name: string;
  displayOrder: number;
  items: CheckListItem[];
}

export interface CompanyCheckListProgress {
  id: string;
  companyId: string;
  checkListItemId: string;
  isCompleted: boolean;
  completedAt?: string;
  completedByUserId?: string;
  isDisregarded?: boolean;
  disregardedAt?: string;
  disregardedByUserId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Joined data from query (PascalCase from backend)
  itemName?: string;
  description?: string;
  responsible?: string;
  deadlineValue?: number;  // Backend returns PascalCase
  deadlineUnit?: string;   // Backend returns PascalCase
  observation?: string;
  itemDisplayOrder?: number;
  topicName?: string;
  topicDisplayOrder?: number;
  companyCreatedAt?: string; // When company was created (for deadline calculation)
  skiped?: boolean; // Item is marked as Skiped
  dependencyItemIds?: string[]; // IDs of items this item depends on
}

export interface UpdateProgressRequest {
  checkListItemId: string;
  isCompleted: boolean;
  isDisregarded?: boolean;
  notes?: string;
}

// Get all topics with items (for admin configuration)
export const getCheckListTopicsWithItems = async (): Promise<CheckListTopic[]> => {
  return checkListAxios.get("/checklist/topics");
};

// Get checklist progress for a specific company
export const getCompanyCheckListProgress = async (
  companyId: string
): Promise<CompanyCheckListProgress[]> => {
  return checkListAxios.get(`/checklist/company/${companyId}/progress`);
};

// Initialize checklist for a new company (Abertura flow)
export const initializeCompanyCheckList = async (
  companyId: string
): Promise<void> => {
  await checkListAxios.post(`/checklist/company/${companyId}/initialize`);
};

// Update progress for a specific checklist item
export const updateCheckListProgress = async (
  companyId: string,
  request: UpdateProgressRequest
): Promise<void> => {
  await checkListAxios.put(`/checklist/company/${companyId}/progress`, request);
};

// ===== TOPIC CRUD =====

export interface CreateTopicRequest {
  name: string;
  displayOrder: number;
}

export interface UpdateTopicRequest {
  name: string;
  displayOrder: number;
}

export const createCheckListTopic = async (request: CreateTopicRequest): Promise<string> => {
  const response = await checkListAxios.post<{ id: string; message: string }>('/checklist/topics', request);
  // Force recompile
  return (response as any).id;
};

export const updateCheckListTopic = async (id: string, request: UpdateTopicRequest): Promise<void> => {
  await checkListAxios.put(`/checklist/topics/${id}`, request);
};

export const deleteCheckListTopic = async (id: string): Promise<void> => {
  await checkListAxios.delete(`/checklist/topics/${id}`);
};

// ===== ITEM CRUD =====

export interface CreateItemRequest {
  topicId: string;
  name: string;
  description?: string;
  responsible: string;
  deadlineValue: number;
  deadlineUnit: string;
  observation?: string;
  displayOrder: number;
  skiped?: boolean;
  dependencyItemIds?: string[];
}

export interface UpdateItemRequest {
  topicId: string;
  name: string;
  description?: string;
  responsible: string;
  deadlineValue: number;
  deadlineUnit: string;
  observation?: string;
  displayOrder: number;
  skiped?: boolean;
  dependencyItemIds?: string[];
}

export const createCheckListItem = async (request: CreateItemRequest): Promise<string> => {
  const response = await checkListAxios.post<{ id: string; message: string }>('/checklist/items', request);
  return (response as any).id;
};

export const updateCheckListItem = async (id: string, request: UpdateItemRequest): Promise<void> => {
  await checkListAxios.put(`/checklist/items/${id}`, request);
};

export const deleteCheckListItem = async (id: string): Promise<void> => {
  await checkListAxios.delete(`/checklist/items/${id}`);
};
